// ── Core data model for CSS topological code editor ──

export interface Qubit {
  id: string;
  index: number;
  x: number;
  y: number;
  label?: string;
}

export interface Edge {
  id: string;
  qubitIds: [string, string];
}

export type PauliType = 'X' | 'Z';

// ── Measurement circuit per stabilizer ──

export type GateType = 'H' | 'CX' | 'CZ' | 'M' | 'R';

export interface MeasurementGate {
  gate: GateType;
  /** Qubit indices: 1 element for H/M/R, 2 for CX/CZ [control, target] */
  targets: number[];
}

export const GATE_QUBIT_COUNT: Record<GateType, number> = {
  H: 1, CX: 2, CZ: 2, M: 1, R: 1,
};

// ── Visual circuit composer model ──

export interface CircuitQubit {
  id: string;
  label: string;
  /** true for ancilla wires, false for data qubit wires */
  isAncilla: boolean;
  /** For data qubits: the global qubit index. For ancillas: unique ancilla index. */
  globalIndex: number;
}

export interface CircuitGate {
  id: string;
  gate: GateType;
  /** Column (time step) in the circuit grid */
  col: number;
  /** Row indices within the circuit's qubit list */
  qubitRows: number[];
}

export interface StabilizerCircuit {
  qubits: CircuitQubit[];
  gates: CircuitGate[];
  /** Which classical bit index stores this stabilizer's syndrome */
  syndromeClassicalBit?: number;
}

export interface Stabilizer {
  id: string;
  name?: string;
  type: PauliType;
  qubitIds: string[];
  color: string;
  /** Visual circuit for measurement. If undefined, auto-generated. */
  circuit?: StabilizerCircuit;
  /** Legacy: flat gate list. Migrated to circuit on edit. */
  measurementCircuit?: MeasurementGate[];
}

/** Single-qubit Pauli in a logical operator */
export type SinglePauli = 'X' | 'Y' | 'Z';

/** One term in a logical operator: a Pauli on a specific qubit */
export interface PauliTerm {
  pauli: SinglePauli;
  qubitIndex: number;
}

/** User-specified logical operator (arbitrary Pauli string) */
export interface LogicalOp {
  id: string;
  /** Label: "X" for logical X̄, "Z" for logical Z̄ (user's choice, just a label) */
  label: string;
  /** The Pauli string: list of (Pauli, qubit) terms */
  terms: PauliTerm[];
}

/** Detector: parity check over a set of measurement outcomes */
export interface Detector {
  id: string;
  name: string;
  /** Indices into the flat measurement list (0-based) */
  measurementIndices: number[];
}

/** Observable: logical measurement outcome as XOR of measurement records */
export interface Observable {
  id: string;
  name: string;
  /** Indices into the flat measurement list (0-based) */
  measurementIndices: number[];
}

export interface StabilizerCode {
  name: string;
  qubits: Qubit[];
  edges: Edge[];
  stabilizers: Stabilizer[];
  nextQubitIndex: number;
  /** Ordered list of stabilizer IDs for measurement scheduling. If undefined, auto-scheduled. */
  scheduleOrder?: string[];
  /** User-specified logical operators */
  logicalOperators?: LogicalOp[];
  /** User-specified detectors */
  detectors?: Detector[];
  /** User-specified observables */
  observables?: Observable[];
}

// ── Editor state ──

export type EditorTool = 'select' | 'pan' | 'addQubit' | 'addEdge' | 'addStabilizer';

export interface EditorState {
  tool: EditorTool;
  selectedQubitIds: Set<string>;
  selectedStabilizerId: string | null;
  hoveredStabilizerId: string | null;
  edgeStartQubitId: string | null;
}

// ── Preset palettes ──

export const STABILIZER_COLORS = [
  '#3b82f6', // blue
  '#ef4444', // red
  '#22c55e', // green
  '#8b5cf6', // purple
  '#06b6d4', // cyan
  '#f97316', // orange
  '#ec4899', // pink
  '#eab308', // yellow
];

export const DEFAULT_X_COLOR = '#8b5cf6'; // purple
export const DEFAULT_Z_COLOR = '#3b82f6'; // blue

// ── Geometry helpers ──

export function centroid(points: { x: number; y: number }[]): { x: number; y: number } {
  const n = points.length;
  if (n === 0) return { x: 0, y: 0 };
  const sum = points.reduce((acc, p) => ({ x: acc.x + p.x, y: acc.y + p.y }), { x: 0, y: 0 });
  return { x: sum.x / n, y: sum.y / n };
}

/** Sort points by angle around their centroid to form a simple polygon */
export function sortByAngle(points: { x: number; y: number }[]): { x: number; y: number }[] {
  const c = centroid(points);
  return [...points].sort(
    (a, b) => Math.atan2(a.y - c.y, a.x - c.x) - Math.atan2(b.y - c.y, b.x - c.x)
  );
}

/** Pad a polygon outward from its centroid by a fixed amount */
export function padPolygon(
  points: { x: number; y: number }[],
  padding: number
): { x: number; y: number }[] {
  const c = centroid(points);
  return points.map((p) => {
    const dx = p.x - c.x;
    const dy = p.y - c.y;
    const len = Math.sqrt(dx * dx + dy * dy) || 1;
    return { x: p.x + (dx / len) * padding, y: p.y + (dy / len) * padding };
  });
}

// ── Factory helpers ──

let _idCounter = 0;
export function generateId(prefix: string): string {
  return `${prefix}_${Date.now()}_${_idCounter++}`;
}

export function createEmptyCode(name = 'Untitled Code'): StabilizerCode {
  return { name, qubits: [], edges: [], stabilizers: [], nextQubitIndex: 0, scheduleOrder: [] };
}

/** Generate default measurement circuit for a stabilizer */
export function defaultMeasurementCircuit(
  stabType: PauliType,
  dataQubitIndices: number[],
  ancillaIndex: number
): MeasurementGate[] {
  const gates: MeasurementGate[] = [];
  if (stabType === 'X') {
    gates.push({ gate: 'H', targets: [ancillaIndex] });
    for (const qi of dataQubitIndices) {
      gates.push({ gate: 'CX', targets: [ancillaIndex, qi] });
    }
    gates.push({ gate: 'H', targets: [ancillaIndex] });
  } else {
    for (const qi of dataQubitIndices) {
      gates.push({ gate: 'CX', targets: [qi, ancillaIndex] });
    }
  }
  gates.push({ gate: 'M', targets: [ancillaIndex] });
  return gates;
}

export function createDefaultEditorState(): EditorState {
  return {
    tool: 'select',
    selectedQubitIds: new Set(),
    selectedStabilizerId: null,
    hoveredStabilizerId: null,
    edgeStartQubitId: null,
  };
}

// ── Visual circuit helpers ──

let _circuitIdCounter = 0;
export function circuitId(prefix = 'cg'): string {
  return `${prefix}_${Date.now()}_${_circuitIdCounter++}`;
}

/** Build a default StabilizerCircuit from a stabilizer's data qubit indices */
export function buildDefaultCircuit(
  stabType: PauliType,
  dataQubitIndices: number[],
  ancillaGlobalIndex: number,
  stabId: string,
): StabilizerCircuit {
  const qubits: CircuitQubit[] = [
    ...dataQubitIndices.map((idx, i) => ({
      id: `${stabId}_d${i}`,
      label: `q${idx}`,
      isAncilla: false,
      globalIndex: idx,
    })),
    {
      id: `${stabId}_a0`,
      label: `a${ancillaGlobalIndex}`,
      isAncilla: true,
      globalIndex: ancillaGlobalIndex,
    },
  ];

  const ancRow = qubits.length - 1;
  const gates: CircuitGate[] = [];
  let col = 0;

  if (stabType === 'X') {
    gates.push({ id: circuitId(), gate: 'H', col: col++, qubitRows: [ancRow] });
    for (let i = 0; i < dataQubitIndices.length; i++) {
      gates.push({ id: circuitId(), gate: 'CX', col: col++, qubitRows: [ancRow, i] });
    }
    gates.push({ id: circuitId(), gate: 'H', col: col++, qubitRows: [ancRow] });
  } else {
    for (let i = 0; i < dataQubitIndices.length; i++) {
      gates.push({ id: circuitId(), gate: 'CX', col: col++, qubitRows: [i, ancRow] });
    }
  }
  gates.push({ id: circuitId(), gate: 'M', col: col, qubitRows: [ancRow] });

  return { qubits, gates };
}

/** Convert StabilizerCircuit → flat MeasurementGate[] for compilation */
export function flattenCircuit(circuit: StabilizerCircuit): MeasurementGate[] {
  const sorted = [...circuit.gates].sort((a, b) => a.col - b.col);
  return sorted.map((g) => ({
    gate: g.gate,
    targets: g.qubitRows.map((r) => circuit.qubits[r]?.globalIndex ?? r),
  }));
}

/** Generate Qiskit code snippet for a single stabilizer circuit */
export function circuitToQiskit(circuit: StabilizerCircuit, stabName: string): string {
  const lines: string[] = [`# ${stabName}`];
  const sorted = [...circuit.gates].sort((a, b) => a.col - b.col);
  const syndromeBit = circuit.syndromeClassicalBit ?? 0;

  // Assign classical bit indices to M gates in column order
  const mGates = sorted.filter((g) => g.gate === 'M');
  const mGateIdToBit = new Map<string, number>();
  mGates.forEach((g, idx) => mGateIdToBit.set(g.id, idx));

  const numCBits = mGates.length;
  if (numCBits > 0) {
    lines.push(`# ${numCBits} classical bit(s), syndrome output: c[${syndromeBit}]`);
  }

  const qStr = (row: number) => {
    const q = circuit.qubits[row];
    return q ? (q.isAncilla ? `anc_${q.globalIndex}` : `q[${q.globalIndex}]`) : `q[${row}]`;
  };
  for (const g of sorted) {
    switch (g.gate) {
      case 'H': lines.push(`qc.h(${qStr(g.qubitRows[0])})`); break;
      case 'CX': lines.push(`qc.cx(${qStr(g.qubitRows[0])}, ${qStr(g.qubitRows[1])})`); break;
      case 'CZ': lines.push(`qc.cz(${qStr(g.qubitRows[0])}, ${qStr(g.qubitRows[1])})`); break;
      case 'M': {
        const bit = mGateIdToBit.get(g.id) ?? 0;
        const marker = bit === syndromeBit ? '  # <- syndrome' : '';
        lines.push(`qc.measure(${qStr(g.qubitRows[0])}, c[${bit}])${marker}`);
        break;
      }
      case 'R': lines.push(`qc.reset(${qStr(g.qubitRows[0])})`); break;
    }
  }
  return lines.join('\n');
}
